// src/components/AdminCategoryAssignmentsTable.tsx
import React, { useState, useEffect } from 'react';
import CategoryService from '../services/category.service';
import '../styles/responsive-tables.css';

// Force file to be a module
export {};

interface AdminCategoryAssignment {
  id: number;
  user_id: number;
  category_id: number;
  first_name: string;
  last_name: string;
  email: string;
  category_title: string;
  role_id: number;
  role_title: string;
}

interface AdminCategoryAssignmentsTableProps {
  refreshTrigger?: number; // Optional prop to trigger refresh
}

const AdminCategoryAssignmentsTable: React.FC<AdminCategoryAssignmentsTableProps> = ({ 
  refreshTrigger = 0 
}) => {
  const [assignments, setAssignments] = useState<AdminCategoryAssignment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<number>(0); // 0 means all roles
  const [categoryFilter, setCategoryFilter] = useState<number>(0); // 0 means all categories
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [assignmentsPerPage] = useState<number>(5);
  const [paginatedAssignments, setPaginatedAssignments] = useState<AdminCategoryAssignment[]>([]);
  
  // Check if current user has the required permission or is a super admin
  useEffect(() => {
    const checkPermission = async () => {
      try {
        // Get current user from localStorage (not sessionStorage)
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          console.log('Current user role:', user.role_id);
          
          // Instead of checking permissions here, let's try the API call
          // and let the backend handle the permission check
          setHasPermission(true); // Allow the API call to proceed
        } else {
          setHasPermission(false);
          setError('User not authenticated');
        }
      } catch (err) {
        console.error('Error checking permissions:', err);
        setHasPermission(false);
        setError('Failed to validate permissions');
      } finally {
        setLoading(false);
      }
    };
    
    checkPermission();
  }, []);
  
  useEffect(() => {
    if (hasPermission) {
      fetchAssignments();
    }
  }, [refreshTrigger, hasPermission]);

  const fetchAssignments = async () => {
    if (!hasPermission) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await CategoryService.getAllAdminCategoryAssignments();
      setAssignments(response || []);
    } catch (err: any) {
      console.error('Error fetching admin-category assignments:', err);
      
      // Handle different types of errors
      if (err?.status === 403 || err?.statusCode === 403) {
        setError('You do not have permission to view admin-category assignments. Please contact your administrator.');
        setHasPermission(false);
      } else if (err?.status === 401 || err?.statusCode === 401) {
        setError('Your session has expired. Please log in again.');
        setHasPermission(false);
      } else if (err?.message?.toLowerCase().includes('permission')) {
        setError('You do not have permission to view admin-category assignments.');
        setHasPermission(false);
      } else {
        setError(err?.message || 'Failed to load assignments');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter assignments based on search term and filters
  const filteredAssignments = assignments.filter((assignment) => {
    const matchesSearch = searchTerm === '' || 
      assignment.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.category_title.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesRole = roleFilter === 0 || assignment.role_id === roleFilter;
    const matchesCategory = categoryFilter === 0 || assignment.category_id === categoryFilter;
    
    return matchesSearch && matchesRole && matchesCategory;
  });

  // Pagination logic
  useEffect(() => {
    const startIndex = (currentPage - 1) * assignmentsPerPage;
    const endIndex = startIndex + assignmentsPerPage;
    setPaginatedAssignments(filteredAssignments.slice(startIndex, endIndex));
  }, [filteredAssignments, currentPage, assignmentsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredAssignments.length / assignmentsPerPage);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, categoryFilter]);

  // Get unique categories for filtering
  const uniqueCategories = Array.from(new Set(assignments.map((a) => a.category_id)))
    .map((categoryId) => {
      const assignment = assignments.find((a) => a.category_id === categoryId);
      return {
        id: categoryId,
        title: assignment ? assignment.category_title : `Category ${categoryId}`
      };
    });

  // Count unique admins in the filtered assignments
  const uniqueAdmins = new Set(filteredAssignments.map((a) => a.user_id)).size;

  return (
    <div className="admin-category-assignments-table">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, email or category"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button 
              className="btn btn-outline-secondary" 
              type="button"
              onClick={() => setSearchTerm('')}
              disabled={!searchTerm}
            >
              Clear
            </button>
          </div>
        </div>
          
        <div className="col-md-3">          <select 
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(parseInt(e.target.value))}
          >
            <option value="0">All Roles</option>
            <option value="2">Admins</option>
            <option value="3">System Incharge</option>
            <option value="4">Developers</option>
            <option value="5">Super Admins</option>
          </select>
        </div>

        <div className="col-md-3">
          <select 
            className="form-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(parseInt(e.target.value))}
          >
            <option value="0">All Categories</option>
            {uniqueCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Stats summary */}
      <div className="alert alert-info">
        <i className="bi bi-info-circle-fill me-2"></i>
        Showing {filteredAssignments.length} category assignments for {uniqueAdmins} admin users.
        {filteredAssignments.length > assignmentsPerPage && (
          ` • Page ${currentPage} of ${totalPages}`
        )}
      </div>
      
      {loading ? (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive table-container-optimized">
          <table className="table table-striped table-hover table-bordered assignments-table">
            <thead className="table-dark">
              <tr>
                <th className="col-id">ID</th>
                <th className="col-user-id">User ID</th>
                <th className="col-category-id">Category ID</th>
                <th className="col-admin-name">Admin Name</th>
                <th className="col-role">Admin Role</th>
                <th className="col-category">Category Name</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAssignments.length > 0 ? (
                paginatedAssignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="col-id">{assignment.id}</td>
                    <td className="col-user-id">{assignment.user_id}</td>
                    <td className="col-category-id">{assignment.category_id}</td>
                    <td className="col-admin-name">{assignment.first_name} {assignment.last_name}</td>                    <td className="col-role">
                      <span className={`badge ${
                        assignment.role_id === 5 ? 'bg-primary' : 
                        assignment.role_id === 3 ? 'bg-success' : 
                        assignment.role_id === 2 ? 'bg-warning' : 
                        assignment.role_id === 4 ? 'bg-info' : 
                        'bg-secondary'
                      }`}>
                        {assignment.role_title}
                      </span>
                    </td>
                    <td className="col-category">{assignment.category_title}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-3">
                    {searchTerm || roleFilter !== 0 || categoryFilter !== 0 ? (
                      <>
                        <p className="mb-1">No matching assignments found.</p>
                        <button 
                          className="btn btn-sm btn-outline-secondary" 
                          onClick={() => {
                            setSearchTerm('');
                            setRoleFilter(0);
                            setCategoryFilter(0);
                          }}
                        >
                          <i className="bi bi-x-circle"></i> Clear Filters
                        </button>
                      </>
                    ) : (
                      <p className="mb-0">No admin-category assignments found.</p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
        
      {/* Pagination Controls */}
      {filteredAssignments.length > assignmentsPerPage && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              Showing {((currentPage - 1) * assignmentsPerPage) + 1} to {Math.min(currentPage * assignmentsPerPage, filteredAssignments.length)} of {filteredAssignments.length} assignments
            </div>
            <nav aria-label="Assignment pagination">
              <ul className="pagination pagination-sm pagination-compact mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i> Previous
                  </button>
                </li>
                
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and pages around current
                    return page === 1 || 
                           page === totalPages || 
                           Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                    
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <li className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        )}
                        <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </li>
                      </React.Fragment>
                    );
                  })
                }
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
    </div>
  );
};

export default AdminCategoryAssignmentsTable;
